import React, { useMemo, useState } from "react";
import "./DateTimePicker2.css";

function overlaps(aStart, aEnd, bStart, bEnd) {
    return aStart < bEnd && aEnd > bStart;
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function toDateInputValue(date) {
    return date.toISOString().split("T")[0];
}

function formatDay(date) {
    return date.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric"
    });
}

function formatTime(time) {
    const [h, m] = time.split(":").map(Number);

    const d = new Date();
    d.setHours(h, m, 0, 0);

    return d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit"
    });
}

export default function SolusekDateTimePicker({
    blockedEvents = [],
    daysToShow = 14,
    durationMinutes = 30,
    minTime = "08:00",
    maxTime = "17:00",
    stepMinutes = 30,
    onChange
}) {
    const today = new Date();
    const startDate = addDays(new Date(), 1);

    const [selectedDate, setSelectedDate] = useState(
        toDateInputValue(startDate)
    );

    const [selectedTime, setSelectedTime] = useState("");

    const days = useMemo(() => {
        return Array.from({ length: daysToShow }, (_, i) => {
            const date = addDays(startDate, i);

            return {
                date,
                value: toDateInputValue(date)
            };
        });
    }, [daysToShow]);

    const times = useMemo(() => {
        const result = [];

        const [minH, minM] = minTime.split(":").map(Number);
        const [maxH, maxM] = maxTime.split(":").map(Number);

        let current = minH * 60 + minM;
        const end = maxH * 60 + maxM;

        while (current < end) {
            const h = Math.floor(current / 60);
            const m = current % 60;

            result.push(
                `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
            );

            current += stepMinutes;
        }

        return result;
    }, [minTime, maxTime, stepMinutes]);

    const isBlocked = (dateValue, timeValue) => {
        const start = new Date(`${dateValue}T${timeValue}:00`);

        const end = new Date(
            start.getTime() + durationMinutes * 60000
        );

        return blockedEvents.some(event => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);

            return overlaps(start, end, eventStart, eventEnd);
        });
    };

    const availableTimes = times.filter(
        t => !isBlocked(selectedDate, t)
    );

    const selectDate = dateValue => {
        setSelectedDate(dateValue);
        setSelectedTime("");

        onChange?.(null);
    };

    const selectTime = timeValue => {
        setSelectedTime(timeValue);

        onChange?.({
            start: `${selectedDate}T${timeValue}:00`,
            end: new Date(
                new Date(`${selectedDate}T${timeValue}:00`).getTime() +
                durationMinutes * 60000
            ).toISOString(),
            durationMinutes
        });
    };

    return (
        <div className="solusek-picker card border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="row g-0">
                <div className="col-md-5 solusek-left">
                    <div className="p-4">
                        <div className="text-uppercase text-muted small fw-bold mb-2">
                            Select Date
                        </div>

                        <h4 className="fw-bold mb-1">
                            Book Appointment
                        </h4>

                        <div className="text-muted mb-4">
                            {durationMinutes} minute appointment
                        </div>

                        <div className="solusek-days">
                            {days.map(day => {
                                const hasAvailable = times.some(
                                    t => !isBlocked(day.value, t)
                                );

                                const active =
                                    selectedDate === day.value;

                                return (
                                    <button
                                        key={day.value}
                                        type="button"
                                        className={
                                            "solusek-day-btn " +
                                            (active ? "active " : "") +
                                            (!hasAvailable
                                                ? "disabled"
                                                : "")
                                        }
                                        disabled={!hasAvailable}
                                        onClick={() =>
                                            selectDate(day.value)
                                        }
                                    >
                                        <span>
                                            {formatDay(day.date)}
                                        </span>

                                        <small>
                                            {hasAvailable
                                                ? "Available"
                                                : "Full"}
                                        </small>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="col-md-7 solusek-right">
                    <div className="p-4">
                        <div className="text-uppercase text-muted small fw-bold mb-2">
                            Select Time
                        </div>

                        <h5 className="fw-bold mb-3">
                            {formatDay(
                                new Date(
                                    `${selectedDate}T00:00:00`
                                )
                            )}
                        </h5>

                        {availableTimes.length === 0 ? (
                            <div className="alert alert-light border">
                                No available times for this date.
                            </div>
                        ) : (
                            <div className="solusek-times">
                                {availableTimes.map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={
                                            "solusek-time-btn " +
                                            (selectedTime === t
                                                ? "active"
                                                : "")
                                        }
                                        onClick={() =>
                                            selectTime(t)
                                        }
                                    >
                                        {formatTime(t)}
                                    </button>
                                ))}
                            </div>
                        )}

                        {selectedTime && (
                            <div className="solusek-confirm mt-4">
                                <div>
                                    <strong>Selected:</strong>{" "}
                                    {formatDay(
                                        new Date(
                                            `${selectedDate}T00:00:00`
                                        )
                                    )}{" "}
                                    at {formatTime(selectedTime)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}